import { createEventBus, type BaseEvent } from '@littlebird/event-bus';

export const USER_DID_SEND_MESSAGE_EVENT = 'user_did_send_message_event';
export const OTHER_WINDOW_DID_SEND_MESSAGE_EVENT =
  'other_window_did_send_message_event';

export const THREAD_DID_BECOME_VISIBLE = 'thread_did_become_visible';
export const THREAD_DID_BECOME_INVISIBLE = 'thread_did_become_invisible';

export const COMPOSER_START_NEW_THREAD = 'COMPOSER_START_NEW_THREAD';

export const COMPOSER_FOCUS_REQUESTED = 'COMPOSER_FOCUS_REQUESTED';

type ComposerStartNewThreadEvent = {
  message: string;
};

type ComposerFocusRequestedEvent = {
  draftId: string;
};

type ThreadVisibilityEvent = {
  threadId: string;
};

export interface LocalChatEventsRegistry {
  [USER_DID_SEND_MESSAGE_EVENT]: {
    threadId: string;
    messageId?: string;
  };
  [OTHER_WINDOW_DID_SEND_MESSAGE_EVENT]: {
    threadId: string;
  };
  [THREAD_DID_BECOME_VISIBLE]: ThreadVisibilityEvent;
  [THREAD_DID_BECOME_INVISIBLE]: ThreadVisibilityEvent;
  [COMPOSER_START_NEW_THREAD]: ComposerStartNewThreadEvent;
  [COMPOSER_FOCUS_REQUESTED]: ComposerFocusRequestedEvent;
}

type LocalChatEventMap = {
  [K in keyof LocalChatEventsRegistry]: BaseEvent & {
    type: K;
    data: LocalChatEventsRegistry[K];
  };
};

export const localChatEventsBus = createEventBus<LocalChatEventMap>({
  name: 'localChatEvents',
});

const CHAT_SENDS_CHANNEL_NAME = 'littlebird-chat-sends';

/**
 * Hover and the main window render the same chats in separate renderers, and
 * the bus only reaches its own renderer. Relay sends so each window's chat
 * view follows a turn sent from the other.
 */
export function relayChatSendsAcrossWindows() {
  const channel = new BroadcastChannel(CHAT_SENDS_CHANNEL_NAME);
  localChatEventsBus.on(USER_DID_SEND_MESSAGE_EVENT, ({ data }) =>
    channel.postMessage({ threadId: data.threadId })
  );
  channel.onmessage = (event: MessageEvent<{ threadId?: string }>) => {
    const threadId = event.data?.threadId;
    if (!threadId) return;
    localChatEventsBus.emit(OTHER_WINDOW_DID_SEND_MESSAGE_EVENT, {
      data: { threadId },
    });
  };
}

/** Calls `listener` for a send from this window or from another window */
export function onChatSendFromAnyWindow(listener: (threadId: string) => void) {
  const subscriptions = [
    localChatEventsBus.on(USER_DID_SEND_MESSAGE_EVENT, ({ data }) =>
      listener(data.threadId)
    ),
    localChatEventsBus.on(OTHER_WINDOW_DID_SEND_MESSAGE_EVENT, ({ data }) =>
      listener(data.threadId)
    ),
  ];
  return {
    unsubscribe: () => {
      for (const subscription of subscriptions) subscription.unsubscribe();
    },
  };
}
